import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { partial, uniqueId } from 'lodash';
import DeferredCtor, { Deferred } from 'promise-deferred';
import { CSSProperties, forwardRef, MouseEventHandler, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaExternalLinkAlt, FaPrint } from 'react-icons/fa';
import { Button, Navbar } from 'reactstrap';
import { EmergencyCard } from '../types/graph';
import CenteredContainer from './CenteredContainer';
import Loading from './Loading';
//import Loading from './Loading';
import { GridLayout } from './ui';
import IFrameContainer, { IFrameContainerImperativeHandle } from './ui/IFrameContainer';

const frameStyle: CSSProperties = {
    height: '100%',
    width: '100%',
    margin: 0,
    padding: 0,
    border: 0,
    overflow: 'auto',
    display: 'block'
}

type EmergencyCardViewerProps = {
    title: string,
    url: string
}

const getEmergencyCardPage = async (url: string) => {

    const baseUrl = new URL("./", url);

    const { data: doc } = await axios.get<HTMLDocument>(url, { responseType: 'document' })

    const idsByUrl: Record<string, string> = {}

    const anchors = Array.from(doc.getElementsByTagName('a'));

    const l = anchors.length

    const promises: Array<Promise<string>> = []

    for (let i = 0; i < l; i++) {
        const anchor = anchors[i]

        const url = new URL(anchor.href, baseUrl).toString()

        const exists = idsByUrl[url] !== undefined

        let id = exists ? idsByUrl[url] : uniqueId('emc-')

        idsByUrl[url] = id

        anchor.setAttribute('data-emc', id)

        if (exists) {
            continue;
        }

        promises.push(axios.get<HTMLDocument>(url, { responseType: 'document' })
            .then(partial((id, { data: doc }) => `<div id="${id}">${doc.body.innerHTML}</div>`, id))
            .catch(partial((id) => `<div id="${id}"></div>`, id)))
    }

    return `<div>${doc.body.innerHTML}</div>${(await Promise.all(promises)).join('')}`
}

export type EmergencyCardViewerImperativeHandle = {
    print: () => Promise<void>
}

const EmergencyCardViewer = forwardRef<EmergencyCardViewerImperativeHandle, EmergencyCardViewerProps>(({ title, url }, ref) => {

    const { t } = useTranslation('default')

    //const [loading, setLoading] = useState<boolean>(true);
    //const [error, setError] = useState<EmergencyCardViewerError | null>(null);

    const iframeRef = useRef<IFrameContainerImperativeHandle>(null);

    const readyDeferredRef = useRef<Deferred<IFrameContainerImperativeHandle> | null>(null)
    if (readyDeferredRef.current === null) {
        readyDeferredRef.current = new DeferredCtor<IFrameContainerImperativeHandle>()
    }

    const { data, isLoading, isError } = useQuery({
        queryKey: ['emergencyCardUrl', url],
        queryFn: () => getEmergencyCardPage(url),
        refetchOnMount: false,
        refetchIntervalInBackground: false,
        refetchOnReconnect: false,
        refetchInterval: Infinity,
        refetchOnWindowFocus: false
    })

    useImperativeHandle(ref, () => ({
        print: () => readyDeferredRef.current!.promise.then((iframe) => iframe.print())
    }), [])

    useEffect(() => {
        const readyDeferred = readyDeferredRef.current!

        return () => {
            try {
                readyDeferred.reject()
            } catch { }
        }
    }, [])

    //const handleIframeAnchorClick = useCallback((ev: Event) => {
    //    const el = ev.target as HTMLElement;

    //    if (el.tagName.toUpperCase() === 'A') {
    //        const { href } = (el as HTMLAnchorElement);

    //        window.open(href);

    //        ev.preventDefault();
    //    }
    //}, []);


    //const handleIframeLoad: ReactEventHandler<HTMLIFrameElement> = useCallback((ev) => {

    //    const iframe = ev.target as HTMLIFrameElement;

    //    if (iframe.src === 'about:blank') {
    //        return;
    //    }


    //    //iframe.contentWindow?.document.body.addEventListener('click', handleIframeAnchorClick);
    //    const iframeDoc = iframe.contentWindow?.document;

    //    if (iframeDoc === undefined) {
    //        return;
    //    }

    //    const anchorsByUrl: Record<string, string> = {};

    //    const firstDiv = iframeDoc.createElement('div');
    //    //firstDiv.style.backgroundColor = iframeDoc.body.getAttribute('bgcolor');
    //    //iframeDoc.body.removeAttribute('bgcolor');

    //    const aOnclick: EventListener = (evt: Event) => {
    //        const ev = evt as MouseEvent;

    //        if (ev.buttons !== 0 || ev.button !== 0) {
    //            return;
    //        }

    //        ev.preventDefault();
    //        const { href } = ev.currentTarget as HTMLAnchorElement;
    //        console.debug(href);
    //        if (iframe.contentWindow === null) {
    //            return;
    //        }



    //        iframe.contentWindow.location.hash = `#`;
    //        iframe.contentWindow.location.hash = `#${anchorsByUrl[href]}`;
    //    };

    //    Array.from(iframeDoc.getElementsByTagName('a')).forEach((a) => {
    //        const { href } = a;

    //        if (!endsWith(href, '.htm') && !endsWith(href, '.html')) {
    //            return;
    //        }

    //        if (!has(anchorsByUrl, href)) {
    //            anchorsByUrl[href] = uniqueId('emc-');
    //        }

    //        //a.href = `#${anchorsByUrl[origA]}`;
    //        a.addEventListener('click', aOnclick);
    //    });

    //    firstDiv.appendChild.apply(firstDiv, [iframeDoc.body.children[0]]);
    //    iframeDoc.body.append(firstDiv);

    //    const parser = new DOMParser();

    //    Promise.all(map(anchorsByUrl, (id, url) => fetch(url).then(res => res.text()).then((txt) => [url, txt] as [string, string]))).then((pr) => {
    //        const resByUrl = fromPairs(pr);

    //        forEach(anchorsByUrl, async (id, url) => {
    //            const html = resByUrl[url];
    //            const { body } = parser.parseFromString(html, 'text/html');
    //            const div = iframeDoc.createElement('div');

    //            div.id = id;
    //            div.innerHTML = body.innerHTML;
    //            //div.style.backgroundColor = body.getAttribute('bgcolor');
    //            iframeDoc.body.appendChild(div);
    //        });

    //        setLoading(false);
    //    });

    //}, [handleIframeAnchorClick, setLoading]);

    const handlePrintButtonClick = useCallback(async () => {
        await readyDeferredRef.current!.promise
        iframeRef.current?.print();
    }, []);

    const header = useMemo(() => {
        if (isLoading === true || isError === true) {
            return null;
        }

        return <Navbar className="navbar ng-white border-bottom box-shadow" light>
            <div className="d-sm-inline-flex flex-sm-row">
                {title}
            </div>
            <div className="navbar-right d-sm-inline-flex flex-sm-row" style={{ gap: '.25em' }}>
                <Button onClick={handlePrintButtonClick}><FaPrint /> {t('Stampa')}</Button>
                <Button tag={'a'} href={url} title={t('Apri in un\'altra scheda').toString()} target="_blank">
                    <FaExternalLinkAlt /><span className="sr-only">{' '}{t('Apri in un\'altra scheda')}</span>
                </Button>
            </div>
        </Navbar>;

    }, [isLoading, isError, t, url, title])

    const containerRef = async (containerDiv: HTMLDivElement | null) => {
        if (containerDiv === null) {
            return;
        }

        const iframe = iframeRef.current!;
        const readyDeferred = readyDeferredRef.current!;

        await Promise.all(Array.from(containerDiv.getElementsByTagName('img')).map((img) => new Promise<void>((resolve) => {
            if (img.complete) {
                resolve()
                return
            }
            img.addEventListener('load', () => resolve())
            img.addEventListener('error', () => resolve())
        })));

        try {
            readyDeferred.resolve(iframe)
        } catch { }
    }

    const iframeHead = useMemo(() => {

        const baseUrl = (new URL("./", url)).toString();

        return <>
            <base href={baseUrl} />
            <title>{title}</title>
            <style>{`
body > div { 
    display: flex;
    flex-direction: column;
    gap: 2em;
}

body > div > div {
    position: relative;
}
`}</style>
        </>
    }, [url, title])

    const onClick = useCallback<MouseEventHandler<HTMLDivElement>>((ev) => {
        ev.preventDefault();
        const emc = (ev.target as HTMLElement).getAttribute('data-emc');

        if (emc === null) {
            return;
        }

        iframeRef.current?.scrollToElement(emc)
    }, [])

    if (isLoading) {
        return <CenteredContainer><Loading /></CenteredContainer>
    }

    return <GridLayout header={header}>
        <IFrameContainer ref={iframeRef} head={iframeHead} style={frameStyle}>
            {data !== undefined && <div ref={containerRef} onClick={onClick} dangerouslySetInnerHTML={{ __html: data }} />}
        </IFrameContainer>
    </GridLayout>;
})

export default EmergencyCardViewer